import {
  Box,
  Breadcrumbs,
  Grid,
  IconButton,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { getDispute } from "slices/disputeSlice";
import Files from "./Files";
import {
  FILES_PAGE_SIZE,
  getFiles,
  getFolder,
  getFolders,
  getProfilesOfDispute,
} from "./FileApi";
import { removeSessionStorage } from "features/tiac_header_waiting/TiacAppHeader";
import { KeyboardBackspace } from "@material-ui/icons";
import { authenticateDocMgmt, isDocMgmtAuthenticated } from "./DocMgmtApi";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "2rem",
    [theme.breakpoints.down("sm")]: {
      marginTop: "1.5rem",
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: "1rem",
    },
  },
  trail: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    textAlign: "left",
  },
  backBtn: {
    padding: theme.spacing(0.5),
    color: TEXT.strong,
  },
  // One line that scrolls sideways when the trail is longer than the screen,
  // collapsed or expanded; names are never cut.
  crumbs: {
    flex: 1,
    minWidth: 0,
    overflowX: "auto",
    fontWeight: 700,
    "& .MuiBreadcrumbs-ol": {
      flexWrap: "nowrap",
    },
    "& .MuiBreadcrumbs-li": {
      flexShrink: 0,
    },
    "& .MuiBreadcrumbs-separator": {
      color: TEXT.strong,
    },
    // A thin purple scrollbar for this strip. Chromium only paints
    // ::-webkit-scrollbar while the standard scrollbar-* properties are unset,
    // and accbScrollbars.css sets them on every element, so they are reset
    // here ("&&" outranks its `html.accb-scrollbars *`). Measured 4px against
    // the global "thin" 10px.
    "&&": {
      scrollbarWidth: "auto",
      scrollbarColor: "auto",
    },
    "&&::-webkit-scrollbar": {
      height: 4,
    },
    "&&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: 4,
    },
    // Firefox has no ::-webkit-scrollbar: the thinnest standard bar, in purple.
    "@supports (-moz-appearance: none)": {
      "&&": {
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.primary.main} transparent`,
      },
    },
  },
  crumbLink: {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: "1rem",
    fontWeight: 700,
    verticalAlign: "bottom",
    "&:hover": {
      color: TEXT.primary,
    },
  },
  crumbText: {
    whiteSpace: "nowrap",
  },
  crumbCurrent: {
    color: TEXT.strong,
    fontSize: "1rem",
    fontWeight: 700,
  },
}));

export default function FileContainer() {
  let { disputeId, folder_id } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const profile = useSelector((state) => state.profile, shallowEqual);

  const isHidePermissionsFeature = true;

  const [dispute, setDispute] = useState();
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  const [parent_folder, setParent_folder] = useState();
  const [allFiles, setAllFiles] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [page, setPage] = useState(1);
  const [filesCount, setFilesCount] = useState(0);
  const [foldersCount, setFoldersCount] = useState(0);

  useEffect(() => {
    dispatch(getDispute(disputeId)).then((res) => {
      const disputeObj = res.results[0];
      setDispute(disputeObj);
      setLoading(false);

      if (!isDocMgmtAuthenticated()) {
        const email = profile?.profile?.user?.email;
        if (email) {
          authenticateDocMgmt(email)
            .then(() => {
              setAuthLoading(false);
            })
            .catch((err) => {
              console.error("Doc mgmt auth failed in FileContainer:", err);
              setAuthLoading(false);
            });
        } else {
          setAuthLoading(false);
        }
      } else {
        setAuthLoading(false);
      }
    });
    getProfiles();
  }, [disputeId]);

  const getProfiles = () => {
    getProfilesOfDispute(disputeId)
      .then((res) => {
        let profiles = [];
        res.data.details.claimant.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Claimant";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.claimant_lawyer.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Claimant's Counsel";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.respondent.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Respondent";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.respondent_lawyer.forEach((item) => {
          let p = item;
          p.profile.profile_type = "Respondent's Counsel";
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        res.data.details.professionals.forEach((item) => {
          let p = item;
          if (p.profile.profile_type === "arbitrator_rv") {
            p.profile.profile_type = "Arbitrator";
          } else if (p.profile.profile_type === "witness") {
            p.profile.profile_type = "Witness";
          } else if (p.profile.profile_type === "witness_rv") {
            p.profile.profile_type = "Barrister";
          } else if (p.profile.profile_type === "expert_rv") {
            p.profile.profile_type = "Solicitor";
          } else {
            p.profile.profile_type = "Mediator";
          }
          if (p.profile.id !== profile.profile.id) profiles.push(p);
        });
        setAllProfiles(profiles);
      })
      .catch((error) => { });
  };

  const getActiveFolderId = () =>
    folder_id === "0" || folder_id === 0 ? dispute?.docs_root_folder_id : folder_id;

  // Fetches one page of rows: up to FILES_PAGE_SIZE folders and FILES_PAGE_SIZE files
  const loadRows = (targetPage) => {
    const activeFolderId = getActiveFolderId();
    if (!activeFolderId) return;

    setAllFiles([]);
    setAllFolders([]);
    getFiles(disputeId, activeFolderId, targetPage, FILES_PAGE_SIZE).then((res) => {
      setAllFiles(res.data);
      setFilesCount(res.count);
    });
    getFolders(disputeId, activeFolderId, targetPage, FILES_PAGE_SIZE).then((res) => {
      setAllFolders(res.data);
      setFoldersCount(res.count);
    });
  };

  const reload = (targetPage = page) => {
    if (!dispute) return;
    const activeFolderId = getActiveFolderId();
    if (!activeFolderId) return;

    loadRows(targetPage);

    if (activeFolderId !== dispute.docs_root_folder_id) {
      getFolder(activeFolderId).then((response) => {
        setParent_folder(response.data[0]);
        if (response.data.length > 0) {
          let routes = getParent(response.data[0]);
          routes = routes.reverse();
          setAllRoutes(routes);
        } else {
          setAllRoutes([]);
        }
      });
    } else {
      setParent_folder(null);
      setAllRoutes([]);
    }
  };

  const getParent = (parent) => {
    let arr = [];
    if (
      String(parent.id) !== String(dispute?.docs_root_folder_id) &&
      parent.name !== "root" &&
      parent.nameToShowUser !== "root"
    ) {
      arr.push({ name: parent.nameToShowUser || parent.name, id: parent.id });
    }
    if (parent.parent_folder) {
      let t = getParent(parent.parent_folder);
      t.forEach((item) => {
        arr.push(item);
      });
    }
    return arr;
  };

  useEffect(() => {
    if (!authLoading && dispute) {
      // Entering a folder always starts from the first page
      setPage(1);
      reload(1);
    }
  }, [folder_id, authLoading, dispute]);

  const handlePageChange = (newPage) => {
    if (newPage === page) return;
    setPage(newPage);
    loadRows(newPage);
  };

  // A trail wider than the screen opens scrolled to its end, so the current
  // folder is in view. Re-run once the folder's path (allRoutes) arrives.
  const crumbsRef = useRef(null);
  useEffect(() => {
    const el = crumbsRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [folder_id, allRoutes, loading, authLoading]);

  if (loading || authLoading) return <CircularCenteredLoader />;

  const isAdminProfile = ["sub_admin_tiac_rv", "case_manager", "admin"].includes(
    profile.profile.profile_type
  );
  // Dashboard / case / Files / each folder down to the current one.
  const crumbs = [
    {
      key: "dashboard",
      label: "Dashboard",
      onClick: () => {
        if (isAdminProfile) removeSessionStorage();
        history.push("/");
      },
    },
    {
      key: "case",
      label: dispute.name,
      onClick: () => {
        if (
          ["sub_admin_tiac_rv", "case_manager"].includes(
            profile.profile.profile_type
          )
        ) {
          sessionStorage.setItem("tiac_overview_caseid", dispute.id);
          history.push(`/`);
        } else if (["admin"].includes(profile.profile.profile_type)) {
          sessionStorage.setItem("caseid", dispute.id);
          history.push(`/`);
        } else history.push(`/disputes/${dispute.id}/`);
      },
    },
    {
      key: "files",
      label: "Files",
      onClick: () => history.push(`/disputes/${dispute.id}/files/0`),
    },
    ...allRoutes.map((route) => ({
      key: route.id,
      label: route.name,
      onClick: () => history.push(`/disputes/${dispute.id}/files/${route.id}`),
    })),
  ];

  return (
    <Grid container justify="center">
      <Grid item lg={10} md={10} sm={11} xs={11} className={classes.root}>
        <Box className={classes.trail}>
          <IconButton
            size="small"
            aria-label="Back"
            className={classes.backBtn}
            onClick={() => {
              if (isAdminProfile) removeSessionStorage();
              history.goBack();
            }}
          >
            <KeyboardBackspace />
          </IconButton>
          {/* Deep folders collapse to Dashboard / … / parent / current; the
              "…" expands the full trail, which scrolls sideways on one line.
              Keyed on the folder: MUI v4 can't collapse it again, so each
              folder opened starts collapsed. */}
          <Breadcrumbs
            key={folder_id}
            ref={crumbsRef}
            maxItems={4}
            itemsBeforeCollapse={1}
            itemsAfterCollapse={2}
            aria-label="breadcrumb"
            className={classes.crumbs}
          >
            {crumbs.map((crumb, index) =>
              index === crumbs.length - 1 ? (
                <Typography
                  key={crumb.key}
                  component="span"
                  aria-current="page"
                  title={crumb.label}
                  className={`${classes.crumbText} ${classes.crumbCurrent}`}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.key}
                  component="button"
                  type="button"
                  title={crumb.label}
                  className={classes.crumbLink}
                  onClick={crumb.onClick}
                >
                  <span className={classes.crumbText}>{crumb.label}</span>
                </Link>
              )
            )}
          </Breadcrumbs>
        </Box>
        <Files
          disputeId={disputeId}
          folder_id={(folder_id === "0" || folder_id === 0) ? dispute?.docs_root_folder_id : folder_id}
          allFiles={allFiles}
          allFolders={allFolders}
          filesCount={filesCount}
          foldersCount={foldersCount}
          page={page}
          onPageChange={handlePageChange}
          reload={reload}
          allProfiles={allProfiles}
          profile={profile.profile}
          parent_folder={parent_folder}
          isHidePermissionsFeature={isHidePermissionsFeature}
          workspaceId={dispute?.docs_workspace_id}
          customDisputeId={dispute?.tiac_custom_dispute_id}
          // A closed case's documents can still be viewed and downloaded, but
          // nothing in them may be added, changed or removed.
          readOnly={dispute?.status === "closed"}
        />
      </Grid>
    </Grid>
  );
}
